import React from 'react';
import { Popover, Button, Typography, message, Tag } from 'antd';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AIQuickFix } from '../../../../types/analyses';
import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { acceptAIQuickFix } from '../../../actions/items/analyses/update';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Text } = Typography;

type AIQuickFixHoverProps = {
  quickFixes: AIQuickFix[];
  children: React.ReactElement;
  referenceId: number;
  refreshSegmentData?: (segmentId: number) => void;
};

const AIQuickFixHover: React.FC<AIQuickFixHoverProps> = ({
  quickFixes,
  children,
  referenceId,
  refreshSegmentData,
}) => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();
  const { acceptingQuickFixes } = useSelector(
    (state: ApplicationState) => state.items.bulkAIQuickFixes
  );
  const relevantQuickFixes = quickFixes.filter(fix => fix.reference_id === referenceId);

  if (!relevantQuickFixes.length) {
    return children;
  }

  const handleAcceptSuggestion = async (quickFix: AIQuickFix) => {
    try {
      await dispatch(acceptAIQuickFix(quickFix));
      message.success(t('quickfix:aiSuggestionAcceptedSuccess'));

      // Refresh segment-specific data after successful acceptance
      if (refreshSegmentData) {
        refreshSegmentData(quickFix.segment_id);
      }
    } catch (error) {
      message.error(t('quickfix:aiSuggestionAcceptError'));
    }
  };

  const popoverContent = (
    <div className="item-analysis__ai-quick-fix-popover">
      <div className="item-analysis__ai-quick-fix-header">
        <Text strong>{t('quickfix:aiQuickFixesAvailable')}</Text>
      </div>
      {relevantQuickFixes.map((quickFix, index) => {
        const isLoading = acceptingQuickFixes.includes(quickFix.reference_id);
        return (
          <div key={index} className="item-analysis__ai-quick-fix-item">
            <div className="item-analysis__ai-quick-fix-comparison">
              <div className="invalid-data">
                <Text type="danger" strong>
                  {t('quickfix:invalid')}
                </Text>
                <Tag className="ml-2" color="error">
                  {quickFix.invalid_data}
                </Tag>
              </div>
              <div className="suggestion">
                <Text type="success" strong>
                  {t('quickfix:suggested')}
                </Text>
                <Tag className="ml-2" color="success">
                  {quickFix.ai_suggestion}
                </Tag>
              </div>
            </div>
            <Button
              type="primary"
              size="small"
              icon={isLoading ? <LoadingOutlined /> : <CheckOutlined />}
              onClick={() => handleAcceptSuggestion(quickFix)}
              className="item-analysis__accept-button"
              loading={isLoading}
              disabled={isLoading}
            >
              {t('quickfix:acceptSuggestion')}
            </Button>
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="hover"
      placement="bottom"
      overlayClassName="item-analysis__ai-quick-fix-overlay"
    >
      {children}
    </Popover>
  );
};

export default AIQuickFixHover;
